import { Component, EventEmitter, inject, Input, OnChanges, OnInit, Output } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { CookieService } from 'ngx-cookie-service';
import { map, Observable, startWith } from 'rxjs';

import { EllipsisPipe } from 'src/app/shared/pipes/ellipsis.pipe';
import { RegionNamePipe } from 'src/app/shared/pipes/region-name.pipe';

import { Country } from 'src/app/types/country';
import { CountryOption } from 'src/app/types/country-option';

import { AsyncPipe, NgForOf, NgIf } from '@angular/common';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import countries from '../../../assets/static/countries.json';

/**
 * country selector component
 *
 * provides a select input with autocompletion for a list of countries
 * list of countries is generated from a static json export out of scope
 *
 * visual representation of country is generated
 * by regionCode depending on user language
 * @class CountrySelectorComponent
 * @typedef {CountrySelectorComponent}
 */
@Component( {
    imports: [
        AsyncPipe,
        MatAutocompleteModule,
        MatFormFieldModule,
        MatInputModule,
        MatButtonModule,
        MatIconModule,
        NgForOf,
        NgIf,
        ReactiveFormsModule
    ],
    selector: 'riege-country-selector',
    templateUrl: './country-selector.component.html',
    styleUrls: ['./country-selector.component.scss'],
    standalone: true
} )
export class CountrySelectorComponent implements OnInit, OnChanges {
    @Input() selectedCountry: string = '';

    /** emits an event when selected option changes */
    @Output() countrySelected = new EventEmitter<string>();

    #cookieService = inject( CookieService );
    ellipsis = inject( EllipsisPipe );

    label = $localize`:@@input-label-country:Country`;

    /** country autocomplete input */
    countryCode = new FormControl( '' );

    /** list of all country codes as options */
    #options: CountryOption[] = [];

    /** list of filtered options, filtered by autocomplete input value */
    filteredOptions: Observable<CountryOption[]> | undefined;

    /** list of all country */
    #countries = countries as Country[];

    /** store current language */
    currentLanguage = '';

    constructor( private regionNamePipe: RegionNamePipe ) {
        /** extract country code from list of countries */
        this.#countries.forEach( ( country ) => {
            const regex = /\w{2}/;
            const validCode = regex.test( country.code );
            if ( validCode && !country.deactivated && country.alpha3 ) {
                const regionName = this.regionNamePipe.transform( country.code );
                const option = {
                    code: country.code,
                    region: regionName
                };
                this.#options.push( option );
            }
        } );

        this.currentLanguage = this.#cookieService.get( 'language' ) || document.documentElement.lang;

        /** sort options by regional name */
        const collator = new Intl.Collator( this.currentLanguage );
        this.#options.sort( ( a, b ) => collator.compare( a.region, b.region ) );
    }

    ngOnChanges(): void {
        if ( this.selectedCountry !== '' && this.selectedCountry !== undefined ) {
            const region = this.regionNamePipe.transform( this.selectedCountry );
            const country = { code: this.selectedCountry, region: region };
            this.countrySelect( country );
        } else {
            this.clearInput();
        }
    }

    ngOnInit() {
        /** triggers autocomplete filter */
        this.filteredOptions = this.countryCode.valueChanges.pipe(
            startWith( '' ),
            map( ( value ) => this.filterCountry( value ?? '' ) )
        );
    }

    /**
     * helper function to clear the input field
     * and trigger reload of all data
     */
    clearInput() {
        this.countryCode.setValue( null );
        this.countrySelected.emit( '' );
    }

    /**
     * emit selected country selection
     * @param option country object
     * @param option.code country code
     * @param option.region country rgion name
     */
    countrySelect( option: { code: string; region: string } ) {
        this.countryCode.setValue( this.ellipsis.transform( option.region, 20 ) );
        this.countrySelected.emit( option.code );
    }

    /**
     * filter list of option by input value
     * @param {string} selectedOption - value of selected option
     * @returns {Array} of countries
     */
    private filterCountry( selectedOption: string ): CountryOption[] {
        const filterValue = selectedOption !== '' ? selectedOption.toLowerCase() : '';
        return this.#options.filter( ( option ) => {
            const countryName = option.region.toLowerCase();
            return countryName.includes( filterValue );
        } );
    }
}
